import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import SessionExpiredDialogComponent from "./SessionExpiredDialog.component";

export default function SessionExpiredDialogContainer() {
  const [open, setOpen] = useState(false);

  // Get token data from Redux store
  const tokenExpiration = useSelector((state) => state.session.expiresIn);
  const accessToken = useSelector((state) => state.session.accessToken);
  const isSessionChecked = useSelector(
    (state) => state.session.isSessionChecked
  );

  // Refs for managing intervals and preventing multiple dialogs
  const intervalRef = useRef(null);
  const dialogShownRef = useRef(false);

  const showDialog = () => {
    if (!dialogShownRef.current) {
      dialogShownRef.current = true;
      setOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Reset dialog state when session is checked and no valid token exists
  useEffect(() => {
    if (isSessionChecked && !accessToken) {
      dialogShownRef.current = false;
      setOpen(false);
    }
  }, [isSessionChecked, accessToken]);

  // Set up axios response interceptor for 401 errors
  useEffect(() => {
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        // Handle response errors - only show dialog if session was checked and we had a valid session
        if (
          error.response &&
          error.response.status === 401 &&
          !dialogShownRef.current &&
          isSessionChecked &&
          accessToken // Only show if we previously had a token
        ) {
          // Check if this is a token expiration issue
          const errorMessage =
            error.response.data?.message || error.response.data?.code || "";
          const isTokenExpired =
            errorMessage.toLowerCase().includes("expired") ||
            errorMessage.toLowerCase().includes("invalid") ||
            errorMessage.toLowerCase().includes("unauthorized") ||
            errorMessage.toLowerCase().includes("token") ||
            error.response.status === 401;

          if (isTokenExpired) {
            showDialog();
          }
        }

        // A 403 is a permission denial, not a session problem, so this interceptor
        // deliberately does NOT act on it: the session-expired dialog only fires for
        // 401 above, and the failing thunk already owns its own alert (the bundle /
        // company middlewares surface the server's "Access denied." message, the
        // loading middleware is the fallback owner for the rest). Alerting here as
        // well would break the "ONE ALERT OWNER PER THUNK" invariant documented in
        // configs/redux/loading/loading.middleware.js and stack a second, generic
        // toast on top of the specific one for a single failed action.

        // Return the error to be handled by the original catch blocks
        return Promise.reject(error);
      }
    );

    // Cleanup function to remove the interceptor when component unmounts
    return () => {
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  // Proactive token expiration monitoring
  useEffect(() => {
    const checkTokenExpiration = () => {
      if (
        !tokenExpiration ||
        !accessToken ||
        dialogShownRef.current ||
        !isSessionChecked
      ) {
        return;
      }

      // Get current timestamp in seconds
      const currentTime = Math.floor(Date.now() / 1000);

      // Check if token has expired or will expire within 5 seconds
      const bufferTime = 5; // 5 seconds buffer
      const timeUntilExpiration = tokenExpiration - currentTime;

      if (timeUntilExpiration <= bufferTime) {
        // Clear the interval to stop further checks
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }

        // Show the session expired dialog
        showDialog();
      }
    };

    // Only start monitoring if we have a valid token, expiration time, and session is checked
    if (
      tokenExpiration &&
      accessToken &&
      !dialogShownRef.current &&
      isSessionChecked
    ) {
      // Check immediately
      checkTokenExpiration();

      // Set up interval to check every 1 second
      intervalRef.current = setInterval(checkTokenExpiration, 1000);
    }

    // Cleanup interval when component unmounts or dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [tokenExpiration, accessToken, isSessionChecked]);

  // Expose the showDialog method globally so it can be called from external sources if needed
  useEffect(() => {
    window.showSessionExpiredDialog = showDialog;

    return () => {
      delete window.showSessionExpiredDialog;
    };
  }, []);

  return <SessionExpiredDialogComponent open={open} onClose={handleClose} />;
}
